import { useEffect, useState } from "react";

type SaveBarApi = {
  show: (id: string) => Promise<void>;
  hide: (id: string) => Promise<void>;
  toggle?: (id: string) => Promise<void>;
  leaveConfirmation?: () => Promise<void>;
};

function api(): SaveBarApi | null {
  try {
    return (
      (
        window as Window & {
          shopify?: { saveBar?: SaveBarApi };
        }
      ).shopify?.saveBar ?? null
    );
  } catch {
    return null;
  }
}

/**
 * Show/hide App Bridge save bar from React dirty state.
 * `debounceMs` delays show while editing (Shopify theme-style: settle a batch
 * then prompt). Hide is always immediate when clean. Leave-page confirm still
 * comes from App Bridge once the bar is shown.
 */
export function useSaveBar(
  id: string,
  dirty: boolean,
  opts?: { debounceMs?: number },
) {
  const debounceMs = opts?.debounceMs ?? 0;
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    if (!dirty) {
      setArmed(false);
      return;
    }
    if (debounceMs <= 0) {
      setArmed(true);
      return;
    }
    const t = window.setTimeout(() => setArmed(true), debounceMs);
    return () => window.clearTimeout(t);
  }, [dirty, debounceMs]);

  useEffect(() => {
    const bar = api();
    if (!bar) return;
    if (armed) void bar.show(id);
    else void bar.hide(id);
    return () => {
      void bar.hide(id);
    };
  }, [id, armed]);
}

export async function hideSaveBar(id: string) {
  await api()?.hide(id);
}

export function snapshotEqual(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}
